import { useState } from 'react';
import { formatMoney } from '@lombok/shared';
import { Button } from '@lombok/ui';
import { useSkus } from '@/data/hooks';
import { cleanQuantity } from '@/features/orders/orderDraft';
import type { OrderCart } from '@/features/orders/useOrderCart';

interface SkuOrderRowsProps {
  productId: string;
  cart: OrderCart;
}

/**
 * The presentations (SKUs) of one catalog product with their price, and a quantity + "Agregar" to put
 * them in the customer's order. Loaded only when the product is expanded. The price is the SKU's base
 * price, the same one the server snapshots when the order is placed.
 */
export function SkuOrderRows({ productId, cart }: SkuOrderRowsProps) {
  const query = useSkus(productId);
  const [quantities, setQuantities] = useState<Record<string, string>>({});

  if (query.isLoading) return <p className="px-4 py-3 text-sm text-text-muted">Cargando presentaciones…</p>;
  if (query.isError) return <p className="px-4 py-3 text-sm text-danger">No se pudieron cargar las presentaciones.</p>;

  const skus = (query.data ?? []).filter((s) => s.active);
  if (skus.length === 0) {
    return <p className="px-4 py-3 text-sm text-text-muted">Este producto no tiene presentaciones disponibles.</p>;
  }

  return (
    <ul className="divide-y divide-border">
      {skus.map((sku) => {
        const text = quantities[sku.skuId] ?? '1';
        const quantity = cleanQuantity(text);
        const inOrder = cart.quantityOf(sku.skuId);
        return (
          <li key={sku.skuId} className="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0">
              <p className="font-medium text-text-primary">{sku.name}</p>
              <p className="text-xs text-text-muted">
                {sku.code}
                {sku.packSize ? ` · ${sku.packSize} por ${sku.unit}` : ` · ${sku.unit}`}
              </p>
              {inOrder > 0 ? <p className="mt-1 text-xs text-success">En tu pedido: {inOrder}</p> : null}
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm font-semibold text-text-primary">{formatMoney(sku.basePriceMinorUsd)}</span>
              <input
                type="number"
                inputMode="numeric"
                min={1}
                aria-label={`Cantidad de ${sku.name}`}
                value={text}
                onChange={(e) => setQuantities((prev) => ({ ...prev, [sku.skuId]: e.target.value }))}
                className="h-10 w-20 rounded-md border border-border-strong bg-surface px-2 text-sm text-text-primary focus-visible:border-brand-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              />
              <Button
                size="sm"
                disabled={!quantity}
                onClick={() => {
                  if (!quantity) return;
                  cart.add({ productId, skuId: sku.skuId, quantity });
                  setQuantities((prev) => ({ ...prev, [sku.skuId]: '1' }));
                }}
              >
                Agregar
              </Button>
            </div>
          </li>
        );
      })}
    </ul>
  );
}
